import produce from 'immer';
import { partition } from 'lodash';

import { InquiryTemplate } from 'dashboard/models/inquiry-template';
import { ThemeSet, ThemeSetStyleMap } from 'dashboard/models/theme-set';
import { isUiStep, StepPreview } from 'lib/interfaces/StepsConfig';
import {
  ButtonType,
  isUiButtonComponent,
  isUiParentComponent,
  UiComponent,
} from 'lib/interfaces/ui-components';

export const mergeStyleIntoStepsPreview = (
  stepsPreview: StepPreview[],
  styleMap: ThemeSetStyleMap,
): StepPreview[] => {
  return stepsPreview.map((step) => {
    return produce(step, (draftStep) => {
      if (step.type in styleMap['steps-styles']) {
        draftStep.styles = styleMap['steps-styles'][step.type];
      }
      if (isUiStep(draftStep)) {
        draftStep.config.components = mergeStyleIntoUIComponents(
          draftStep.config.components,
          styleMap['components-styles'],
        );
      }
    });
  });
};

const mergeStyleIntoUIComponents = (
  uiComponents: UiComponent[],
  componentsStyleMap: ThemeSetStyleMap['components-styles'],
): UiComponent[] => {
  return uiComponents.map((component) => {
    return produce(component, (draftComponent) => {
      if (
        isUiButtonComponent(component) &&
        component.attributes?.['button-type'] === ButtonType.Secondary
      ) {
        draftComponent.styles = componentsStyleMap['button_secondary'];
      } else if (component.type in componentsStyleMap) {
        draftComponent.styles = componentsStyleMap[component.type];
      }

      if (isUiParentComponent(draftComponent)) {
        draftComponent.attributes.children = mergeStyleIntoUIComponents(
          draftComponent.attributes.children,
          componentsStyleMap,
        );
      }
    });
  });
};

/**
 * Partitions the list of given `inquiryTemplates` into two groups:
 * those that use the given `themeSet` as its default theme, and those that don't.
 * @returns a tuple representing [inUseInquiryTemplates, notInUseInquiryTemplates]
 */
export const partitionInUseInquiryTemplates = (
  themeSet: ThemeSet,
  inquiryTemplates: InquiryTemplate[],
  options?: { sortByNameDisplay?: boolean },
): [InquiryTemplate[], InquiryTemplate[]] => {
  const inUseInquiryTemplateIds =
    themeSet.relationships['inquiry-templates'].data.map((it) => it.id) || [];
  const orderedInquiryTemplates = options?.sortByNameDisplay
    ? [...inquiryTemplates].sort((a, b) =>
        a.attributes['name-display'].localeCompare(b.attributes['name-display']),
      )
    : inquiryTemplates;
  return partition(orderedInquiryTemplates, (it) => inUseInquiryTemplateIds.includes(it.id));
};
